import { put, select, take, takeEvery } from "redux-saga/effects";
import { API } from "../../services";
import type { Habit, UserHabit } from "../types";
import { getIsAuthenticated, getIsKnown } from "../../user/selectors";
import type { State } from "../../store";

/** over time, we should be able to remove this entirely as the destinations will be stored
 with the habits when they are created */
import { FREEFORM, MULTIDAY, SINGLESITE } from "../../navigation";
import type { HabitDestination } from "../../navigation";

function getDestinationForHabit(habit): HabitDestination | null {
  if (habit == null) {
    return null;
  }

  if (habit.type === "multi-day") {
    return MULTIDAY;
  }

  if (habit.type === "single-site") {
    return SINGLESITE;
  }

  return FREEFORM;
}

function getUserHabits(habits, installtimems, installed): UserHabit[] {
  return habits.map((habit) => {
    return {
      habit: {
        destination: getDestinationForHabit(habit),
        key: habit.habitId,
        ...habit,
      },
      installtimems,
      installed,
      /*, paid: false*/
    };
  });
}

function getLocalUserHabit(userHabit): UserHabit {
  return {
    ...userHabit,
    habit: {
      destination: getDestinationForHabit(userHabit.habit),
      ...userHabit.habit,
      params: {
        description: userHabit.habit.description,
        title: userHabit.habit.title,
        sectionType: userHabit.habit.habitId,
        extras: userHabit.habit.extras,
      },
      key: userHabit.habit.habitId,
    },
  };
}

function* fetchInstalledHabits(action) {
  try {
    let result = yield API.get("rugged-brain-api", "/user-habits", null);
    let installedHabits: UserHabit[] | null | undefined = result
      .filter((userHabit) => userHabit.habit != null)
      .map(getLocalUserHabit);
    yield put({
      type: "LOAD_HABITS_SUCCEEDED",
      installedHabits,
    });
  } catch (e) {
    console.error(e);
    yield put({
      type: "LOAD_HABITS_FAILED",
      message: e.message,
    });
  }
}

function getQueryFilter(query: string | null | undefined) {
  return (userHabit) => {
    return (
      query == null ||
      query === "" ||
      userHabit.habit.title.toLowerCase().includes(query.toLowerCase()) ||
      userHabit.habit.description.toLowerCase().includes(query.toLowerCase())
    );
  };
}

function* fetchAvailableHabits(action) {
  try {
    // most of this needs to be transferred to the server
    // for now we get both, but the action is typed with an `installed` flag
    // that will allow us to load either the installed or available habits
    const time = yield select((state: State) => state.time.time);
    const query = action == null ? null : action.query;
    const installedHabits = yield select((state: State) =>
      state.habits.habits.filter(getQueryFilter(query))
    );
    // const queryParamsString =
    //   query != null && query.length > 0
    //     ? "?query=" + encodeURIComponent(query)
    //     : "";
    // we need to improve "available" as a query
    // const result = yield API.get(
    //   "rugged-brain-api",
    //   "/habits/available" + queryParamsString
    // );
    const myResult = yield API.get("rugged-brain-api", "/habits/my", null);
    const remoteHabits: UserHabit[] = getUserHabits(
      myResult,
      time,
      false
    ).filter(getQueryFilter(query));
    const knownKeys = new Set(remoteHabits.map((h) => h.habit.habitId));
    const additionalHabits = installedHabits.filter(
      (h) => !knownKeys.has(h.habit.habitId)
    );
    const availableHabits: UserHabit[] = [...remoteHabits, ...additionalHabits];
    yield put({
      type: "LOAD_AVAILABLE_HABITS_SUCCEEDED",
      availableHabits,
      query: query,
    });
  } catch (e) {
    console.error(e);
    yield put({
      type: "LOAD_AVAILABLE_HABITS_FAILED",
      message: e.message,
    });
  }
}

function* removeHabit(action) {
  try {
    const allHabits = yield select((state: State) => state.habits.habits);
    const userHabit = allHabits.find(
      (installedHabit) => installedHabit.habit.habitId === action.habitId
    );
    const installedHabits = allHabits.filter(
      (installedHabit) => installedHabit.habit.habitId !== action.habitId
    );
    yield API.del("rugged-brain-api", "/user-habits", {
      body: {
        habitId: userHabit.habitId,
      },
    });
    yield put({
      type: "STORE_HABITS_SUCCEEDED",
      installedHabits,
    });
  } catch (e) {
    console.error(e);
    yield put({
      type: "STORE_HABITS_FAILED",
      message: e.message,
    });
  }
}

function* installHabit(action) {
  try {
    const allHabits = yield select(
      (state: State) => state.availableHabits.allHabits
    );
    const habit = allHabits[action.habitId];

    if (habit == null) {
      throw new Error("habit not found " + action.habitId);
    }

    if (habit.userId != null && habit.createdms != null) {
      const result = yield API.post("rugged-brain-api", "/user-habits", {
        body: {
          ownerUserId: habit.userId,
          habitCreatedms: habit.createdms,
        },
      });
      const installedHabit = getLocalUserHabit(result);
      const installedHabits = yield select(
        (state: State) => state.availableHabits.installedHabits
      );
      yield put({
        type: "STORE_HABITS_SUCCEEDED",
        installedHabits: [...installedHabits, installedHabit],
      });
    } else {
      throw new Error("misconfigured habit");
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: "STORE_HABITS_FAILED",
      message: e.message,
    });
  }
}

function* createHabit(action) {
  const isAuthenticated = yield select(getIsAuthenticated);

  if (!isAuthenticated) {
    console.error("User is not authenticated");
    return;
  }

  try {
    const result: Habit = yield API.post("rugged-brain-api", "/habits", {
      body: action.habit,
    });

    if (action.install === true) {
      yield API.post("rugged-brain-api", "/user-habits", {
        body: {
          ownerUserId: result.userId,
          habitCreatedms: result.createdms,
        },
      });
    }

    action.callback();
    yield put({
      type: "CREATE_HABITS_SUCCEEDED",
    });
    yield fetchAvailableHabits(null);
    yield fetchInstalledHabits(null);
  } catch (e) {
    console.error(e);
    yield put({
      type: "CREATE_HABITS_FAILED",
      message: e.message,
    });
  }
}

function* deleteHabit(action) {
  const isAuthenticated = yield select(getIsAuthenticated);

  if (!isAuthenticated) {
    console.error("User is not authenticated");
    return;
  }

  try {
    yield API.del("rugged-brain-api", "/habits", {
      body: {
        createdms: action.habit.createdms,
      },
    });

    if (action.callback != null) {
      action.callback();
    }

    yield put({
      type: "DELETE_HABITS_SUCCEEDED",
    });
    yield fetchAvailableHabits(null);
  } catch (e) {
    console.error(e);
    yield put({
      type: "DELETE_HABITS_FAILED",
      message: e.message,
    });
  }
}

function* updateHabit(action) {
  const isAuthenticated = yield select(getIsAuthenticated);

  if (!isAuthenticated) {
    console.error("User is not authenticated");
    return;
  }

  try {
    const habit = {
      ...action.habit,
      createdms: action.existingHabit.createdms,
    };
    yield API.put("rugged-brain-api", "/habits", {
      body: habit,
    });
    action.callback();
    yield put({
      type: "UPDATE_HABIT_SUCCEEDED",
      habit: habit,
    });
  } catch (e) {
    console.error(e);
    yield put({
      type: "UPDATE_HABIT_FAILED",
      message: e.message,
    });
  }

  yield fetchAvailableHabits(null);
}

function* loadHabit(action) {
  const isKnown = yield select(getIsKnown);

  if (!isKnown) {
    take("LOGIN_LOADED");
  }

  // const isAuthenticated = yield select(getIsAuthenticated);
  // const endpoint = isAuthenticated
  //   ? "/habits/" + action.userId + "/" + action.createdms
  //   : "/public/habits/" + action.userId + "/" + action.createdms;
  const endpoint = "/habits/" + action.userId + "/" + action.createdms;

  //load the public API version
  try {
    const habit = yield API.get("rugged-brain-api", endpoint, null);
    yield put({
      type: "LOAD_HABIT_SUCCESS",
      habit,
    });
  } catch (e) {
    console.error(e);
    console.error(e.message);
    yield put({
      type: "LOAD_HABIT_FAILED",
      message: e.message,
    });
  }
}

function* logSaga() {
  yield takeEvery("LOAD_INSTALLED_HABITS", fetchInstalledHabits);
  yield takeEvery("LOAD_AVAILABLE_HABITS", fetchAvailableHabits);
  yield takeEvery("REMOVE_HABIT", removeHabit);
  yield takeEvery("INSTALL_HABIT", installHabit);
  yield takeEvery("CREATE_HABIT", createHabit);
  yield takeEvery("DELETE_HABIT", deleteHabit);
  yield takeEvery("UPDATE_HABIT", updateHabit);
  yield takeEvery("LOAD_HABIT", loadHabit);
}

export default logSaga;
